import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "contexts/AuthContext";
import Icon from "@mui/material/Icon";

const toWaPhone = (phone) => {
  const d = (phone || "").replace(/\D/g, "");
  return d.startsWith("0") ? "233" + d.slice(1) : d;
};
import PropTypes from "prop-types";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import StatusChip from "components/StatusChip";
import Swal from "sweetalert2";
import { API_BASE, API_HOST } from "config/api";

// ---------------------------------
// Offering occasions
// ---------------------------------

export const OFFERING_OCCASIONS = [
  { value: "sunday", label: "Sunday Offering" },
  { value: "all_night", label: "All-Night Service" },
  { value: "midweek", label: "Midweek Service" },
  { value: "harvest", label: "Harvest/Thanksgiving" },
  { value: "special", label: "Special Offering" },
  { value: "other", label: "Other" },
];

function occasionLabel(payment) {
  if (payment.type !== "offering") return null;
  const match = OFFERING_OCCASIONS.find((o) => o.value === payment.occasion);
  return match ? match.label : null;
}

// ---------------------------------
// Cell Components
// ---------------------------------

const SerialCell = ({ value }) => (
  <Typography variant="caption" color="text.secondary" fontWeight={700}>
    {value}
  </Typography>
);
SerialCell.propTypes = { value: PropTypes.any.isRequired };

const StatusCell = ({ value }) => (
  <Box ml={-1}>
    <StatusChip value={value} color={value === "active" ? "success" : "error"} />
  </Box>
);
StatusCell.propTypes = { value: PropTypes.string.isRequired };

const ActionsCell = ({ row, handleEdit, handleDelete, handleNotify }) => (
  <Box display="flex" justifyContent="center" gap={1} flexWrap="wrap">
    <Button variant="contained" color="info" size="small" onClick={() => handleEdit(row.original)}>
      <Icon>edit</Icon>&nbsp;Edit
    </Button>
    <Button
      variant="contained"
      color="success"
      size="small"
      onClick={() => handleNotify(row.original)}
    >
      <Icon>notifications</Icon>&nbsp;Notify
    </Button>
    <Button
      variant="contained"
      color="error"
      size="small"
      onClick={() => handleDelete(row.original)}
    >
      <Icon>delete</Icon>&nbsp;Delete
    </Button>
  </Box>
);
ActionsCell.propTypes = {
  row: PropTypes.shape({ original: PropTypes.object }).isRequired,
  handleEdit: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
  handleNotify: PropTypes.func.isRequired,
};

// ---------------------------------
// Main Hook
// ---------------------------------

export default function usePaymentData() {
  const { user, church } = useAuth();
  const searchTimeoutRef = useRef(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [members, setMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showMemberDropdown, setShowMemberDropdown] = useState(false);

  const [paymentData, setPaymentData] = useState({
    columns: [
      { Header: "SN", accessor: "serial", align: "center", width: "5%", Cell: SerialCell },
      {
        Header: "Contributor",
        accessor: "contributorLabel",
        align: "left",
        width: "20%",
        Cell: SerialCell,
      },
      { Header: "Type", accessor: "type", align: "center", width: "10%", Cell: SerialCell },
      {
        Header: "Occasion",
        accessor: "occasionLabel",
        align: "center",
        width: "15%",
        Cell: SerialCell,
      },
      { Header: "Amount", accessor: "amount", align: "center", width: "15%", Cell: SerialCell },
      {
        Header: "Payment Method",
        accessor: "method",
        align: "center",
        width: "15%",
        Cell: SerialCell,
      },
      {
        Header: "Reference",
        accessor: "reference",
        align: "center",
        width: "15%",
        Cell: SerialCell,
      },
      {
        Header: "Payment Date",
        accessor: "paymentDate",
        align: "center",
        width: "15%",
        Cell: SerialCell,
      },
      { Header: "Status", accessor: "status", align: "center", width: "15%", Cell: StatusCell },
      {
        Header: "Actions",
        accessor: "actions",
        align: "center",
        width: "25%",
        Cell: (props) => (
          <ActionsCell
            {...props}
            handleEdit={handleEdit}
            handleDelete={handleDelete}
            handleNotify={handleNotify}
          />
        ),
      },
    ],
    rows: [],
    isLoading: true,
    error: null,
  });

  // 🔄 Search members with debounce
  // 🔄 Search members with debounce
  const searchMembers = useCallback(
    async (query) => {
      if (!church?.id || !query.trim()) {
        setFilteredMembers([]);
        return;
      }

      setIsLoadingMembers(true);
      try {
        const token = localStorage.getItem("authToken");
        if (!token) return;
        const response = await fetch(
          `${API_BASE}/members/${church.id}/search?query=${encodeURIComponent(query)}`,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        if (result.success) {
          setFilteredMembers(result.data || []);
        } else {
          throw new Error(result.message || "Failed to search members");
        }
      } catch (err) {
        console.error("Search failed:", err);
        setFilteredMembers([]);
        Swal.fire({
          title: "Search Error",
          text: err.message,
          icon: "error",
        });
      } finally {
        setIsLoadingMembers(false);
      }
    },
    [church?.id]
  );

  // Debounced search effect
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredMembers([]);
      setShowMemberDropdown(false);
      return;
    }

    const timer = setTimeout(() => {
      searchMembers(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, searchMembers]);

  // Debounced search effect
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredMembers([]);
      setShowMemberDropdown(false);
      return;
    }

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      searchMembers(searchTerm);
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm, searchMembers]);

  // 🔄 Load all members (initial load)
  const loadAllMembers = useCallback(async () => {
    if (!church?.id) return;

    setIsLoadingMembers(true);
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return;
      const response = await fetch(`${API_BASE}/members/${church.id}`, {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) throw new Error("Failed to fetch members");

      const data = await response.json();
      console.log("Members loaded:", data);
      setMembers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load members:", err);
    } finally {
      setIsLoadingMembers(false);
    }
  }, [church?.id]);

  const handleMemberSelect = (member) => {
    setSelectedPayment((prev) => ({
      ...prev,
      member_id: member.id,
      member_name: `${member.first_name} ${member.surname}`,
      member: member,
    }));
    setSearchTerm(`${member.first_name} ${member.surname}`);
    setShowMemberDropdown(false);
  };

  // ✏️ Edit
  const handleEdit = useCallback((payment) => {
    const original = payment.originalData || {};
    setSelectedPayment({
      ...payment,
      type: original.type || payment.type?.toLowerCase(),
      member_id: original.member_id || "",
      contributor_name: original.meta?.contributor_name || "",
      occasion: original.meta?.occasion || "sunday",
      paymentDate: original.payment_date
        ? new Date(original.payment_date).toISOString().split("T")[0]
        : new Date().toISOString().split("T")[0],
    });
    setSearchTerm(
      original.member ? `${original.member.first_name} ${original.member.surname}` : ""
    );
    setEditModalOpen(true);
  }, []);

  // ❌ Delete
  const handleDelete = useCallback((payment) => {
    setSelectedPayment(payment);
    setDeleteModalOpen(true);
  }, []);

  // 🔔 Notify member via SMS / WhatsApp / Email
  const handleNotify = useCallback((payment) => {
    const name = payment.contributorLabel || "Contributor";
    const original = payment.originalData || {};
    const phone = original.member?.phone || "";
    const email = original.member?.email || "";
    const occasion = payment.occasionLabel || "offering";
    const msg = `Dear ${name}, your ${occasion} of GHS ${payment.amount} has been received on ${payment.paymentDate}. God bless you!`;
    Swal.fire({
      title: `Notify ${name}`,
      html: `
        <p style="margin:4px 0 16px;color:#555;font-size:13px;text-align:left">"${msg}"</p>
        <div style="display:flex;gap:8px;justify-content:center;flex-wrap:wrap">
          <button type="button" id="sn-sms" style="background:#1976d2;padding:8px 20px;border:none;border-radius:6px;color:#fff;cursor:pointer;font-size:14px;font-weight:600">📱 SMS</button>
          <button type="button" id="sn-wa" style="background:#25D366;padding:8px 20px;border:none;border-radius:6px;color:#fff;cursor:pointer;font-size:14px;font-weight:600">💬 WhatsApp</button>
          <button type="button" id="sn-email" style="background:#0288d1;padding:8px 20px;border:none;border-radius:6px;color:#fff;cursor:pointer;font-size:14px;font-weight:600">✉️ Email</button>
        </div>
      `,
      showConfirmButton: false,
      showCancelButton: true,
      cancelButtonText: "Cancel",
      didOpen: (popup) => {
        popup.querySelector("#sn-sms")?.addEventListener("click", () => {
          Swal.close();
          if (!phone) {
            Swal.fire("No phone", `${name} has no phone number on file.`, "warning");
            return;
          }
          const token = localStorage.getItem("authToken");
          fetch(`${API_BASE}/v3/sms/send`, {
            method: "POST",
            headers: {
              Accept: "application/json",
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ recipient: phone, message: msg, type: "payment_receipt" }),
          })
            .then(() =>
              Swal.fire({
                title: "Sent!",
                text: `SMS sent to ${name}.`,
                icon: "success",
                timer: 2000,
              })
            )
            .catch(() => Swal.fire("Error", "Failed to send SMS.", "error"));
        });
        popup.querySelector("#sn-wa")?.addEventListener("click", () => {
          Swal.close();
          const cleaned = toWaPhone(phone);
          if (!cleaned) {
            Swal.fire("No phone", `${name} has no phone number on file.`, "warning");
            return;
          }
          window.open(`https://wa.me/${cleaned}?text=${encodeURIComponent(msg)}`, "_blank");
        });
        popup.querySelector("#sn-email")?.addEventListener("click", () => {
          Swal.close();
          if (!email) {
            Swal.fire("No email", `${name} has no email address on file.`, "warning");
            return;
          }
          window.open(
            `mailto:${email}?subject=Offering%20Receipt&body=${encodeURIComponent(msg)}`,
            "_blank"
          );
        });
      },
    });
  }, []);

  // ✅ Save Payment
  const handleEditSubmit = useCallback(async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return;

      if (
        !selectedPayment.member_id &&
        !selectedPayment.contributor_name &&
        !occasionLabel(selectedPayment)
      ) {
        Swal.fire({
          title: "Missing info",
          text: "Select a member, or enter who/what this contribution is for (e.g. a contributor name).",
          icon: "warning",
        });
        return;
      }

      const url = selectedPayment.id
        ? `${API_BASE}/payments/${selectedPayment.id}`
        : `${API_BASE}/payments`;
      const method = selectedPayment.id ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          member_id: selectedPayment.member_id || null,
          contributor_name: selectedPayment.member_id
            ? null
            : selectedPayment.contributor_name || occasionLabel(selectedPayment),
          occasion: selectedPayment.type === "offering" ? selectedPayment.occasion || null : null,
          type: selectedPayment.type || "tithe",
          amount: selectedPayment.amount,
          method: selectedPayment.method || "manual",
          reference: selectedPayment.reference || "",
          payment_date: selectedPayment.paymentDate,
          status: selectedPayment.status === "active",
          church_id: church.id,
        }),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => null);
        const detail =
          body?.errors && Object.values(body.errors).flat()[0]
            ? Object.values(body.errors).flat()[0]
            : body?.message;
        throw new Error(detail || `HTTP error! status: ${response.status}`);
      }

      Swal.fire({
        title: "Success!",
        text: `Payment successfully ${selectedPayment.id ? "updated" : "created"}`,
        icon: "success",
        confirmButtonText: "OK",
      });

      fetchPayments();
      setEditModalOpen(false);
    } catch (error) {
      console.error("Submit failed:", error);
      Swal.fire({
        title: "Error!",
        text: error.message,
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  }, [selectedPayment, church?.id]);

  // ✅ Delete Confirm
  const handleDeleteConfirm = useCallback(async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return;

      const response = await fetch(`${API_BASE}/payments/${selectedPayment.id}`, {
        method: "DELETE",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      Swal.fire({
        title: "Success!",
        text: "Payment deleted successfully",
        icon: "success",
        confirmButtonText: "OK",
      });

      fetchPayments();
      setDeleteModalOpen(false);
    } catch (error) {
      console.error("Delete failed:", error);
      Swal.fire({
        title: "Error!",
        text: error.message,
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  }, [selectedPayment]);

  // ➕ Add New
  const handleAddPayment = useCallback(() => {
    setSelectedPayment({
      id: null,
      member_id: "",
      contributor_name: "",
      occasion: "sunday",
      type: "offering",
      amount: "",
      method: "manual",
      reference: "",
      paymentDate: new Date().toISOString().split("T")[0],
      status: "active",
    });
    setSearchTerm("");
    setFilteredMembers([]);
    setEditModalOpen(true);
  }, []);

  const handlePrint = useCallback(() => window.print(), []);

  // 🔁 Fetch All Payments
  const fetchPayments = useCallback(async () => {
    try {
      setPaymentData((prev) => ({ ...prev, isLoading: true }));
      if (!user || !church) return;

      const token = localStorage.getItem("authToken");
      if (!token) return;

      const response = await fetch(`${API_BASE}/payments/church/${church.id}`, {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const result = await response.json();
      const payments = result.data || result.payments || result;

      setPaymentData((prev) => ({
        ...prev,
        rows: payments.map((pay, index) => ({
          id: pay.id,
          serial: index + 1,
          contributorLabel: pay.member
            ? `${pay.member.first_name || ""} ${pay.member.surname || ""}`.trim()
            : pay.meta?.contributor_name || "Collective",
          type: pay.type.toUpperCase(),
          occasionLabel:
            OFFERING_OCCASIONS.find((o) => o.value === pay.meta?.occasion)?.label ||
            (pay.meta?.occasion ?? "—"),
          amount: pay.amount,
          method: pay.method,
          reference: pay.reference,
          paymentDate: new Date(pay.payment_date).toLocaleDateString(),
          status: pay.status ? "active" : "inactive",
          createdAt: pay.created_at,
          originalData: pay,
          phone: pay.member?.phone || "",
          email: pay.member?.email || "",
        })),
        isLoading: false,
        error: null,
      }));
    } catch (error) {
      console.error("Fetch failed:", error);
      setPaymentData((prev) => ({ ...prev, isLoading: false, error: error.message }));
    }
  }, [user, church]);

  // Initial data load
  useEffect(() => {
    if (church?.id) {
      fetchPayments();
      loadAllMembers();
    }
  }, [church?.id, fetchPayments, loadAllMembers]);

  return {
    ...paymentData,
    members,
    isLoadingMembers,
    filteredMembers,
    handleMemberSelect,
    handleEdit,
    handleDelete,
    handleNotify,
    handlePrint,
    editModalOpen,
    setEditModalOpen,
    deleteModalOpen,
    setDeleteModalOpen,
    selectedPayment,
    setSelectedPayment,
    handleEditSubmit,
    handleDeleteConfirm,
    handleAddPayment,
    searchTerm,
    setSearchTerm,
    loadAllMembers,
    showMemberDropdown,
    setShowMemberDropdown,
  };
}
